import type { CatalogueLine, SessionCommand } from '@fitting-room/contracts'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * Which catalogue she is looking at, and the way back.
 *
 * Present whenever the boutique offers more than one, not only after an offer
 * has been made: a bride who was moved to the floor by a rule she never asked
 * for must be able to go back without knowing that a rule exists. That is the
 * whole reason this is a permanent control rather than part of the offer.
 *
 * The lines and their words are the tenant's — this file has no idea that one
 * of them is made to order and the other is a rail.
 */
export function LineSwitcher({
  lines,
  activeKey,
  locale,
  disabled,
  onChange,
}: {
  readonly lines: readonly CatalogueLine[]
  readonly activeKey: string | undefined
  readonly locale: string
  readonly disabled: boolean
  readonly onChange: (command: SessionCommand) => void
}) {
  const t = uiStrings(locale)
  if (lines.length < 2) return null

  // No answer means the first line, which is what the server resolves it to.
  const active = activeKey ?? lines[0]?.key

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1" role="group" aria-label={t.whichCatalogue}>
      {lines.map((line, index) => {
        const on = line.key === active
        return (
          <button
            key={line.key}
            type="button"
            className="shrink-0 border px-3 py-1.5 text-[0.8125rem] tracking-wide whitespace-nowrap"
            aria-pressed={on}
            disabled={disabled}
            data-line={line.key}
            style={{
              borderColor: on ? 'var(--fr-primary)' : 'var(--fr-rule)',
              background: on ? 'color-mix(in srgb, var(--fr-primary) 18%, transparent)' : 'transparent',
              color: on ? 'var(--fr-ink)' : 'var(--fr-ink-dim)',
              borderRadius: 'var(--fr-radius)',
            }}
            onClick={() => {
              if (on) return
              // The first line is the default, and the default is expressed as
              // "no answer" — so returning to it clears her choice rather than
              // pinning a key that a flow could later rename out from under it.
              onChange({ type: 'set_line', lineKey: index === 0 ? null : line.key })
            }}
          >
            {text(line.label, locale)}
          </button>
        )
      })}
    </div>
  )
}
